import React, { InputHTMLAttributes } from 'react';

import PropTypes from 'prop-types';

import { MagnifyingGlassIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  DetailedStylableComponent,
} from '@adsk/alloy-react-theme';

import useCharCount from '../../hooks/useCharCount';
import { useTranslation } from '../../i18n';

export type SearchInputProps = Omit<
  DetailedStylableComponent<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'ref'
> & {
  showIcon?: boolean;
  isOpen: boolean;
  disabled?: boolean;
  placeholder?: string;
  showCount?: boolean;
  countDisplayThreshold?: number;
  softLimit?: number;
  maxLength?: number;
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      style,
      className,
      showIcon = true,
      placeholder: placeholderProp,
      showCount = false,
      countDisplayThreshold,
      softLimit = 15,
      maxLength,
      isOpen,
      value,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const defaultPlaceholder = isOpen ? t('SEARCH') : t('SELECT');

    const placeholder = placeholderProp ?? defaultPlaceholder;

    const { charCount, shouldRenderCount } = useCharCount({
      showCount,
      softLimit,
      maxLength,
      countDisplayThreshold,
      value,
    });

    return (
      <div
        css={[
          {
            display: 'flex',
            flex: 'auto',
            alignItems: 'center',
            overflow: 'hidden',
          },
          style,
        ]}
        className={className}
      >
        <MagnifyingGlassIcon
          size={20}
          style={[
            {
              minWidth: 20,
              marginLeft: `-${theme.spacing.XXXS}px`,
              marginRight: theme.spacing.XXXS,
              color: theme.tokens.colors.icon.tertiary.value,
              display: showIcon ? 'inline' : 'none',
            },
          ]}
        />
        <input
          ref={ref}
          data-testid={'Dropdown__input'}
          css={[
            {
              flexGrow: 1,
              overflow: 'hidden',
              border: 'none',
              outline: 'none',
              color: 'inherit',
              '&::placeholder': {
                color: props.disabled
                  ? theme.tokens.colors.text.inactive.value
                  : theme.tokens.colors.text.tertiary.value,
              },
              backgroundColor: 'transparent',
              ...(!isOpen && {
                cursor: 'pointer',
              }),
              ...(props.disabled && {
                cursor: 'not-allowed',
              }),
              minWidth: 0,
            },
            theme.typography.labelMedium,
          ]}
          placeholder={placeholder}
          maxLength={maxLength}
          value={value}
          {...props}
        />
        {shouldRenderCount ? (
          <div data-testid="SearchInput__count">
            {`${charCount}/${softLimit || maxLength}`}
          </div>
        ) : null}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Input placeholder text */
  placeholder: PropTypes.string,
  /** Whether to show the search icon */
  showIcon: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  countDisplayThreshold: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
};

export default SearchInput;
